import getClient from 'api';
import {
  addCustomError,
  addGlobalError,
  addInvoices,
  addNextInvoice,
  addPaymentMethods,
  clearInvoices,
  clearNextInvoiceDate,
  FULFILLED_STATUS,
  startLoading,
  stopLoading,
} from 'components';
import {
  INTERNAL_SERVER_ERROR_CODE,
  INVOICE_STATUS,
  MAKE_PAYMENT_ERROR_MESSAGE,
  NO_INVOICE_MESSAGE,
  PAYMENTS_PATH,
  PAYMENT_DEFAULT_PATH,
} from 'constant';
import { invoicePayload, populateInvoicePayload } from 'utils';

import * as actions from './summaryConstants';

const client = getClient();

//ADD_DEFAULT_PAYMENT_METHOD
export const addDefaultPaymentMethod = payload => ({
  type: actions.ADD_DEFAULT_PAYMENT_METHOD,
  payload,
});

/**
 * Clear default payment method data
 */
export const clearDefaultPaymentMethod = () => ({
  type: actions.ADD_DEFAULT_PAYMENT_METHOD,
});

/**
 * Get default payment method.
 *
 * @param {string} billToSiteNumber
 */
export const startAddDefaultPaymentMethod = billToSiteNumber => async dispatch => {
  dispatch(startLoading());

  return client
    .get(PAYMENT_DEFAULT_PATH, { params: { billToSiteNumber } })
    .then(response => {
      dispatch(addDefaultPaymentMethod(response.data));
      dispatch(stopLoading());
    })
    .catch(error => {
      dispatch(addGlobalError(error));
      dispatch(stopLoading());
    });
};

//ADD_DEFAULT_PAYMENT_METHOD
export const addNextivaPlan = payload => ({
  type: actions.ADD_NEXTIVA_PLAN,
  payload,
});

/**
 * Clear nextiva plan data
 */
export const clearNextivaPlan = () => ({
  type: actions.CLEAR_NEXTIVA_PLAN,
});

export const startAddNextivaPlan = billToSiteNumber => dispatch => {
  dispatch(startLoading());

  return client
    .get(
      `/api/platform-billing-orchestration/summary/plan?billToSiteNumber=${billToSiteNumber}`
    )
    .then(response => {
      dispatch(addNextivaPlan(response.data));
      dispatch(stopLoading());
    })
    .catch(error => {
      error?.response?.status === INTERNAL_SERVER_ERROR_CODE
        ? dispatch(addCustomError(NO_INVOICE_MESSAGE))
        : dispatch(addGlobalError(error));

      dispatch(stopLoading());
    });
};

//ADD_CONTACT_INFO
export const addAddContactInfo = payload => ({
  type: actions.ADD_CONTACT_INFO,
  payload,
});

export const startAddContactInfo = () => dispatch => {
  dispatch(startLoading());

  return client
    .get(`/api/platform-admin/v1/company/retrieve`)
    .then(response => {
      dispatch(addAddContactInfo(response.data));
      dispatch(stopLoading());
    })
    .catch(error => {
      dispatch(addGlobalError(error));
      dispatch(stopLoading());
    });
};

//ADD_DEFAULT_PAYMENT_BILL
export const addNextivaBill = payload => ({
  type: actions.ADD_NEXTIVA_BILL,
  payload,
});

/**
 * Clear nextiva bill data
 */
export const clearNextivaBill = () => ({
  type: actions.CLEAR_NEXTIVA_BILL,
});

export const startAddNextivaBill = billToSiteNumber => dispatch => {
  dispatch(startLoading());

  return client
    .get(
      `/api/platform-billing-orchestration/summary/billing?billToSiteNumber=${billToSiteNumber}`
    )
    .then(({ data }) => {
      const isPaymentDisabled =
        !data?.hasOverage && INVOICE_STATUS.CLOSED === data?.invoiceStatus;
      const isPaymentMethodCapabilityEnabled = data?.isPaymentSupported;

      dispatch(addNextivaBill(data));
      dispatch(setPaymentDisabled(isPaymentDisabled));
      dispatch(
        setPaymentMethodCapabilityEnabled(isPaymentMethodCapabilityEnabled)
      );
      dispatch(stopLoading());
    })
    .catch(error => {
      error?.response?.status === INTERNAL_SERVER_ERROR_CODE
        ? dispatch(addCustomError(NO_INVOICE_MESSAGE))
        : dispatch(addGlobalError(error));

      dispatch(stopLoading());
    });
};

//ADD_PAYMENT_TYPE_OBJECT
export const addPaymentTypeObject = payload => ({
  type: actions.ADD_PAYMENT_TYPE_OBJECT,
  payload,
});

//ADD_PAYMENT_TYPE_OBJECT
export const clearPaymentTypeObject = () => ({
  type: actions.CLEAR_PAYMENT_TYPE_OBJECT,
});

export const processNextivaBill = () => ({
  type: actions.PROCESS_NEXTIVA_BILL,
});

export const startProcessNextivaBill = payload => dispatch => {
  dispatch(startLoading());

  return client
    .post(
      `/api/platform-billing-orchestration/summary/billing/card`,
      {},
      {
        params: {
          amount: payload.amount,
          paymentType: payload.paymentType,
          payoff: payload.payoff,
          billToSiteNumber: payload.billToSiteNumber,
        },
      }
    )
    .then(() => {
      dispatch(processNextivaBill());
      dispatch(stopLoading());
    })
    .catch(error => {
      dispatch(addCustomError(MAKE_PAYMENT_ERROR_MESSAGE));
      dispatch(stopLoading());
      return error;
    });
};

export const getSummaryData = billToSiteNumber => dispatch => {
  dispatch(startLoading());

  const dispatchSummaryData = [
    data => dispatch(addNextivaBill(data)),
    data => dispatch(addAddContactInfo(data)),
    data => dispatch(addDefaultPaymentMethod(data)),
    data => dispatch(addNextInvoice(data)),
    data => dispatch(addPaymentMethods(data)),
    data => dispatch(addNextivaPlan(data)),
    data =>
      dispatch(
        addInvoices(populateInvoicePayload({ ...invoicePayload }, { data }))
      ),
  ];

  return Promise.allSettled([
    client.get(
      `/api/platform-billing-orchestration/summary/billing?billToSiteNumber=${billToSiteNumber}`
    ),
    client.get('/api/platform-admin/v1/company/retrieve'),
    client.get(PAYMENT_DEFAULT_PATH, { params: { billToSiteNumber } }),
    client.get(
      `/api/platform-billing-orchestration/invoice/next?billToSiteNumber=${billToSiteNumber}`
    ),
    client.get(PAYMENTS_PATH, { params: { billToSiteNumber } }),
    client.get(
      `/api/platform-billing-orchestration/summary/plan?billToSiteNumber=${billToSiteNumber}`
    ),
    client.get(
      `/api/platform-billing-orchestration/summary/invoices?billToSiteNumber=${billToSiteNumber}`
    ),
  ])
    .then(results => {
      results.forEach((result, index) => {
        if (result.status === FULFILLED_STATUS) {
          dispatchSummaryData[index](result.value.data);
        }
      });
      dispatch(stopLoading());
    })
    .catch(error => {
      error?.response?.status === INTERNAL_SERVER_ERROR_CODE
        ? dispatch(addCustomError(NO_INVOICE_MESSAGE))
        : dispatch(addGlobalError(error));

      dispatch(stopLoading());
    });
};

/**
 * Redux action to modify the paymentDisabled flag
 *
 * @param {bool} payload bool value to be set
 */
export const setPaymentDisabled = payload => ({
  type: actions.SET_PAYMENT_DISABLED,
  payload,
});

/**
 * Redux action to modify the payment method capability flag
 *
 * @param {bool} payload bool value to be set
 */
export const setPaymentMethodCapabilityEnabled = payload => ({
  type: actions.SET_PAYMENT_METHOD_CAPABILITY_ENABLED,
  payload,
});

/**
 * Clear all summary cards content
 */
export const clearSummaryCards = () => dispatch => {
  dispatch(clearNextivaBill());
  dispatch(clearDefaultPaymentMethod());
  dispatch(clearNextInvoiceDate());
  dispatch(clearInvoices());
  dispatch(clearNextivaPlan());
};
